import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchCompanyUsers } from '../data-provider/company-users';
import type { CompanyUser, CompanyUsers, CompanyUsersFilters } from '../models/company-users.model';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const companyUsersQueryOptions = (
  filters: CompanyUsersFilters,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const users = await fetchCompanyUsers(filters, { signal });
      put(users.list, { companyId: filters.companyId });
      return users;
    },
    queryKey: CACHE_KEYS.companyUsers({ companyId: filters.companyId }),
    select: (users) => {
      const searchQuery = filters.search?.name?.trim();
      if (!searchQuery) {
        return users;
      }

      if (!searchQuery) {
        return users;
      }
      const regex = new RegExp(escapeRegExp(searchQuery), 'i');

      return {
        ...users,
        list: users.list.filter((u) => regex.test(JSON.stringify(u))),
      };
    },
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_COMPANY_USERS_CACHE_KEY = btoa('internal-company-users-cache-key');

const INTERNAL_QUERY_COMPANY_USERS_KEY = (companyId: string) =>
  companyUsersQueryOptions({ companyId, name: INTERNAL_COMPANY_USERS_CACHE_KEY }).queryKey;

const getCompanyUserFallback = (companyUserId: string): CompanyUser => {
  logger.warn(`CompanyUser "${companyUserId}" was not found. Verify the data model.`);
  return {
    email: companyUserId,
    fullName: companyUserId,
    id: companyUserId,
    label: companyUserId,
    lastName: companyUserId,
    name: companyUserId,
  };
};

export const put = async (companyUsers: CompanyUser[], { companyId }: { companyId: string }) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_COMPANY_USERS_KEY(companyId));

  const newCompanyUsers = companyUsers.reduce(
    (acc, user) => {
      acc[String(user.id)] = user;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_COMPANY_USERS_KEY(companyId), {
    list: Object.values(newCompanyUsers),
    map: newCompanyUsers,
  });
};

export const getAll = ({ companyId }: { companyId: string }): CompanyUsers | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_COMPANY_USERS_KEY(companyId));
};

export const find = async (companyUserId: string, { companyId }: { companyId: string }): Promise<CompanyUser> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_COMPANY_USERS_KEY(companyId));

  const user = data?.map[companyUserId];
  if (user) {
    return user;
  }

  try {
    const newData = await queryClient.query(companyUsersQueryOptions({ companyId, search: { id: companyUserId } }));
    if (newData.map[companyUserId]) {
      void put(newData.list, { companyId });
      return newData.map[companyUserId];
    }
  } catch {
    return getCompanyUserFallback(companyUserId);
  }

  return getCompanyUserFallback(companyUserId);
};

export const findAll = async (
  companyUserIds: string[],
  { companyId }: { companyId: string },
): Promise<CompanyUser[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_COMPANY_USERS_KEY(companyId));

  const missingIds: string[] = [];
  const companyUsers: CompanyUser[] = [];

  for (const companyUserId of companyUserIds) {
    const c = data?.map[companyUserId];
    if (c) {
      companyUsers.push(c);
    } else {
      missingIds.push(companyUserId);
    }
  }

  if (!missingIds.length) {
    return companyUsers;
  }

  try {
    const newData = await queryClient.query(
      companyUsersQueryOptions({ companyId, search: { id: missingIds.join(',') } }),
    );
    void put(newData.list, { companyId });
    for (const missingId of missingIds) {
      const c = newData.map[missingId];
      if (c) {
        companyUsers.push(c);
      } else {
        companyUsers.push(getCompanyUserFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      companyUsers.push(getCompanyUserFallback(missingId));
    }
  }

  return companyUsers;
};
